/**
 * `/admin` — admin landing page with the compliance dashboard embedded
 * (M13, PRD 06 §Dashboard).
 *
 * On mount we fire both `GET /admin/me` and `GET /admin/dashboard` in
 * parallel so the page hydrates in one round of I/O. Both gates share
 * the same 401 handler — a stale session on either call sends the admin
 * back to `/admin/sign-in` with `replace: true` so the back button
 * doesn't bounce into a half-loaded page.
 *
 * Layout (mobile-first, PRD 06 §Accessibility):
 *   - Header: greeting + email + Sign out
 *   - Four compliance snapshot sections (new this week, expiring in 30,
 *     expired and overdue, stood down), each a card with the total count
 *     and up to 10 rows. "+ N more" appears when the server truncates.
 *   - NQA ITS sync panel — due this week or overdue
 *   - Worker-job health — one row per registered job with its last
 *     success + dead-letter count; the panel header surfaces the total
 *     dead-letter count so an admin notices a red number before drilling in.
 *
 * Drill-down is out of this slice — "view all" links would point at
 * `/admin/compliance-records?view=...` etc., which land in later M13
 * slices. The panel headings give admins the shape of the work even
 * before the drill-down surfaces exist.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type {
  AdminDashboardResponse,
  AdminMeResponse,
  DashboardComplianceRow,
  DashboardComplianceSection,
  DashboardNqaItsRow,
  DashboardNqaItsSection,
  CronHealthEntry,
} from '@isms/shared';

export interface AdminHomePageProps {
  client: ApiClient;
  /** Where to send the admin after sign-out / on 401. Defaults to
   *  `/admin/sign-in` — tests override to decouple from the router. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; profile: AdminMeResponse; dashboard: AdminDashboardResponse };

export function AdminHomePage({
  client,
  signInPath = '/admin/sign-in',
}: AdminHomePageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [signOutPending, setSignOutPending] = useState(false);
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [profile, dashboard] = await Promise.all([
        client.getAdminMe(),
        client.getAdminDashboard(),
      ]);
      setState({ kind: 'ready', profile, dashboard });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load the dashboard. Please check your connection and try again.",
      });
    }
  }, [client, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSignOut(): Promise<void> {
    setSignOutPending(true);
    try {
      await client.adminLogout();
    } catch {
      // Even if sign-out fails server-side (stale cookie, network blip),
      // sending the admin to the sign-in page is the safe default.
    } finally {
      redirectToSignIn();
    }
  }

  if (state.kind === 'loading') {
    return (
      <main className="admin-home" aria-busy="true">
        <p className="admin-home-loading">Loading your admin profile…</p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="admin-home">
        <div className="admin-home-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </main>
    );
  }

  const { email, full_name } = state.profile;
  const { dashboard } = state;

  return (
    <main className="admin-home" aria-labelledby="admin-home-heading">
      <header className="admin-home-header">
        <div>
          <h1 id="admin-home-heading">Hi {full_name}</h1>
          <p className="admin-home-email">{email}</p>
        </div>
        <button
          type="button"
          className="admin-home-signout-button"
          onClick={() => void handleSignOut()}
          disabled={signOutPending}
        >
          {signOutPending ? 'Signing out…' : 'Sign out'}
        </button>
      </header>

      <p className="admin-home-run-date">
        Dashboard as of{' '}
        <time dateTime={toIsoString(dashboard.run_date)}>{toIsoString(dashboard.run_date)}</time>{' '}
        (Sydney).
      </p>

      <nav className="admin-home-nav" aria-label="Admin sections">
        <Link to="/admin/people" className="admin-home-nav-link">
          View all people
        </Link>
        <Link to="/admin/worker-jobs" className="admin-home-nav-link">
          Worker jobs
          {dashboard.worker_jobs.unretried_mail_failures_count +
            dashboard.worker_jobs.stuck_audit_packs_count >
          0
            ? ` (${
                dashboard.worker_jobs.unretried_mail_failures_count +
                dashboard.worker_jobs.stuck_audit_packs_count
              } needs attention)`
            : ''}
        </Link>
        <Link to="/admin/exports" className="admin-home-nav-link">
          Recent exports
        </Link>
        <Link to="/admin/compliance-obligations" className="admin-home-nav-link">
          Obligations
        </Link>
        <Link to="/admin/documents" className="admin-home-nav-link">
          Policies
        </Link>
        <Link to="/admin/users" className="admin-home-nav-link">
          Admin users
        </Link>
      </nav>

      <div className="admin-home-sections">
        <ComplianceSection
          heading="New this week"
          emptyCopy="No new compliance records in the last 7 days."
          section={dashboard.new_this_week}
          columnMode="created"
          viewFilter="new_this_week"
        />
        <ComplianceSection
          heading="Expiring in 30 days"
          emptyCopy="No records are due to expire in the next 30 days."
          section={dashboard.expiring_in_30}
          columnMode="expiry"
          viewFilter="expiring_in_30"
        />
        <ComplianceSection
          heading="Expired and overdue"
          emptyCopy="No records are overdue."
          section={dashboard.expired_overdue}
          columnMode="overdue"
          viewFilter="expired_overdue"
        />
      </div>

      <NqaItsSection section={dashboard.nqa_its_needs_sync} />

      <WorkerJobsSection
        cronHealth={dashboard.worker_jobs.cron_health}
        unretriedMailFailuresCount={dashboard.worker_jobs.unretried_mail_failures_count}
        stuckAuditPacksCount={dashboard.worker_jobs.stuck_audit_packs_count}
      />
    </main>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

interface ComplianceSectionProps {
  heading: string;
  emptyCopy: string;
  section: DashboardComplianceSection;
  /** Which column the row contributes alongside the name + item name. */
  columnMode: 'created' | 'expiry' | 'overdue';
  /** The `?view=` the "View all" link passes to /admin/compliance-records. */
  viewFilter: 'new_this_week' | 'expiring_in_30' | 'expired_overdue';
}

function ComplianceSection({
  heading,
  emptyCopy,
  section,
  columnMode,
  viewFilter,
}: ComplianceSectionProps): JSX.Element {
  return (
    <section className="dashboard-section" aria-labelledby={`section-${slug(heading)}`}>
      <header className="dashboard-section-header">
        <h2 id={`section-${slug(heading)}`}>{heading}</h2>
        <span className="dashboard-section-count" aria-label={`${section.total_count} in total`}>
          {section.total_count}
        </span>
      </header>
      {section.rows.length === 0 ? (
        <p className="dashboard-section-empty">{emptyCopy}</p>
      ) : (
        <ul className="dashboard-section-list">
          {section.rows.map((row) => (
            <li key={row.record_id} className="dashboard-section-row">
              <span className="dashboard-row-name">{row.full_name}</span>
              <span className="dashboard-row-item">{row.requirement_name}</span>
              <span className="dashboard-row-detail">{formatRowDetail(row, columnMode)}</span>
            </li>
          ))}
        </ul>
      )}
      {section.truncated ? (
        <p className="dashboard-section-truncated">
          + {section.total_count - section.rows.length} more
        </p>
      ) : null}
      {section.total_count > 0 ? (
        <p className="dashboard-section-cta">
          <Link to={`/admin/compliance-records?view=${viewFilter}`}>View all →</Link>
        </p>
      ) : null}
    </section>
  );
}

function formatRowDetail(
  row: DashboardComplianceRow,
  mode: 'created' | 'expiry' | 'overdue',
): string {
  if (mode === 'created') {
    return `Created ${toIsoString(row.created_at).slice(0, 10)}`;
  }
  if (row.expiry_date == null) {
    return 'No expiry on file';
  }
  if (mode === 'expiry') {
    const d = row.days_until_expiry;
    if (d == null) return `Expires ${row.expiry_date}`;
    if (d === 0) return `Expires today`;
    if (d === 1) return `Expires in 1 day`;
    return `Expires in ${d} days`;
  }
  // overdue
  const d = row.days_until_expiry;
  if (d == null) return `Expiry ${row.expiry_date}`;
  const n = Math.abs(d);
  if (n === 0) return `Expired today`;
  if (n === 1) return `Overdue by 1 day`;
  return `Overdue by ${n} days`;
}

interface NqaItsSectionProps {
  section: DashboardNqaItsSection;
}

function NqaItsSection({ section }: NqaItsSectionProps): JSX.Element {
  return (
    <section className="dashboard-section" aria-labelledby="section-nqa-its">
      <header className="dashboard-section-header">
        <h2 id="section-nqa-its">Volunteers needing NQA ITS sync</h2>
        <span className="dashboard-section-count" aria-label={`${section.total_count} in total`}>
          {section.total_count}
        </span>
      </header>
      {section.rows.length === 0 ? (
        <p className="dashboard-section-empty">
          Every volunteer's NQA ITS portal entry is up to date.
        </p>
      ) : (
        <ul className="dashboard-section-list">
          {section.rows.map((row) => (
            <li key={row.person_id} className="dashboard-section-row">
              <Link to={`/admin/people/${row.person_id}`} className="dashboard-row-name">
                {row.full_name}
              </Link>
              <span className="dashboard-row-detail">{formatNqaDetail(row)}</span>
            </li>
          ))}
        </ul>
      )}
      {section.truncated ? (
        <p className="dashboard-section-truncated">
          + {section.total_count - section.rows.length} more
        </p>
      ) : null}
    </section>
  );
}

function formatNqaDetail(row: DashboardNqaItsRow): string {
  if (row.nqa_its_synced_at == null) {
    return 'Never synced to NQA ITS';
  }
  return `Changed since last sync (${toIsoString(row.nqa_its_synced_at)})`;
}

interface WorkerJobsSectionProps {
  cronHealth: CronHealthEntry[];
  unretriedMailFailuresCount: number;
  stuckAuditPacksCount: number;
}

function WorkerJobsSection({
  cronHealth,
  unretriedMailFailuresCount,
  stuckAuditPacksCount,
}: WorkerJobsSectionProps): JSX.Element {
  const totalAlerts = unretriedMailFailuresCount + stuckAuditPacksCount;
  return (
    <section className="dashboard-section" aria-labelledby="section-worker-jobs">
      <header className="dashboard-section-header">
        <h2 id="section-worker-jobs">Cron health</h2>
        <span
          className={`dashboard-section-count ${totalAlerts > 0 ? 'dashboard-count-alert' : ''}`}
          aria-label={`${totalAlerts} failure modes need attention`}
        >
          {unretriedMailFailuresCount} mail · {stuckAuditPacksCount} audit pack
        </span>
      </header>
      {totalAlerts > 0 ? (
        <p className="dashboard-section-cta">
          <Link to="/admin/worker-jobs">Review failures →</Link>
        </p>
      ) : null}
      {cronHealth.length === 0 ? (
        <p className="dashboard-section-empty">No worker jobs have run yet.</p>
      ) : (
        <ul className="dashboard-section-list">
          {cronHealth.map((entry) => (
            <li key={entry.job_name} className="dashboard-section-row">
              <span className="dashboard-row-name">{entry.job_name}</span>
              <span className="dashboard-row-item">
                {formatLastSuccess(entry.last_succeeded_at)}
              </span>
              <span className="dashboard-row-detail">{entry.stale ? 'Stale' : 'Healthy'}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatLastSuccess(iso: string | Date | null): string {
  if (iso == null) return 'Never succeeded';
  return `Last success ${toIsoString(iso).slice(0, 10)}`;
}

function toIsoString(v: string | Date): string {
  return typeof v === 'string' ? v : v.toISOString();
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
